import debounce from 'lodash.debounce'
import { ReactElement, useContext, useEffect, useState } from 'react'
import { FormContext } from '../FormContext'
import { FormValidationHandler } from '../types'

export interface FormControllerProps<V> {
  name: string
  onValidate?: FormValidationHandler<V>
  render: (props: { value: V; onValueChange: (value: V) => void }) => ReactElement
}

export const FormController = <V,>(props: FormControllerProps<V>) => {
  const [validating, setValidating] = useState<boolean>(false)
  const formContext = useContext(FormContext)

  const validate = async (newValue: V) => {
    setValidating(true)
    if (props.onValidate) {
      const error = await props.onValidate(newValue)
      formContext.setErrors((draft: any) => {
        if (error === null || error === undefined) {
          delete draft[props.name]
        } else {
          draft[props.name] = error
        }
      })
    }
    setValidating(false)
  }
  const debounceValidate = debounce(validate, 100)

  const onValueChange = async (newValue: V) => {
    formContext.setFormData((draft) => {
      draft[props.name] = newValue
    })
    await validate(newValue)
  }

  // have to run the validator once to populate the errors dictionary
  useEffect(() => {
    onValueChange(formContext.formData[props.name])
  }, [])

  // run the validator whenever a form value changes
  useEffect(() => {
    if (validating) return
    debounceValidate(formContext.formData[props.name])
  }, [JSON.stringify(Object.values(formContext.formData))])

  return props.render({ value: formContext.formData[props.name], onValueChange, ...props })
}
